import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  styled,
  Grid,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getVehicles, deleteVehicles } from '../../services/vehicleCalls';
import { VehiclesUpload } from '../vehiclesUpload';

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'styleProps',
})(({ styleProps }) => ({
  background: styleProps?.backgroundColor ?? undefined,
  borderRight: '1px solid #ddd',
  fontWeight: 'bold',
  lineHeight: '0.1',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledSearchBar = styled(TextField)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: '50%',
  },
}));

const StyledTableContainer = styled(TableContainer)({
  maxHeight: 'calc(85vh - 140px)',
  overflowY: 'auto',
  marginTop: '20px',
  borderRadius: '10px',
  boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
});

const StyledButton = styled(Button)({
  marginBottom: '10px',
  width: '100%',
  '@media (min-width:600px)': {
    width: 'auto',
  },
});

const ROWS_PER_PAGE = 25;

export const VehicleTable = () => {
  const inputRef = useRef(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE);
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [openSheetModal, setOpenSheetModal] = useState(false);
  const [selected, setSelected] = useState([]);

  const toggleSheetModal = useCallback(() => {
    setOpenSheetModal(!openSheetModal);
  }, [openSheetModal]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      setTimeout(() => {
        getVehicles((err, data) => {
          if (err) {
            setVehicles([]);
          } else {
            setVehicles(data);
          }
          setLoading(false);
        });
      }, 1000);
    } catch (error) {
      setVehicles([]);
      setLoading(false);
      console.error('Error fetching vehicles:', error);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    getFilteredVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  const handleSubmit = useCallback(() => {
    setOpenSheetModal(false);
    setShowAlert(true);
    fetchVehicles();
  }, []);

  const handleChangePage = useCallback((_, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    // Allow only numeric input (for the search field)
    if (/^\d*$/.test(value)) {
      setSearchTerm(value);
    }
  }, []);

  const getFilteredVehicles = useCallback(() => {
    if (!searchTerm) {
      setFilteredVehicles(
        filteredVehicles.length > 0 ? filteredVehicles : vehicles,
      );
    } else {
      const results = vehicles.filter((vehicle) => {
        return (
          vehicle.registration?.toLowerCase().includes(searchTerm) ||
          vehicle.bankName?.toLowerCase().includes(searchTerm)
        );
      });
      setSearchTerm('');
      setFilteredVehicles(results);
    }
  }, [filteredVehicles, searchTerm, vehicles]);

  const handleSearch = useCallback(() => {
    getFilteredVehicles();
  }, [getFilteredVehicles]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      handleSearch(); // Trigger search when Enter key is pressed
    }
  }, [handleSearch]);

  useEffect(() => {
    getFilteredVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelected = filteredVehicles.map((vehicle) => vehicle.id);
        setSelected(newSelected);
        return;
      }
      setSelected([]);
    },
    [filteredVehicles],
  );

  const handleSelectClick = useCallback(
    (_, id) => {
      const selectedIndex = selected.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
        );
      }
      setSelected(newSelected);
    },
    [selected],
  );

  const handleDeleteSelected = useCallback(() => {
    deleteVehicles(selected, (err, res) => {
      if (res) {
        setSelected([]);
        fetchVehicles();
      } else {
        console.log('Error deleting records: ', err);
      }
    });
  }, [selected]);

  const paginatedVehicles = useMemo(() => {
    return filteredVehicles.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredVehicles, page, rowsPerPage]);

  const tableBody = useMemo(() => {
    if (loading) {
      return (
        <StyledTableRow>
          <StyledTableCell sx={{ textAlign: 'center' }} colSpan={5}>
            <CircularProgress />
          </StyledTableCell>
        </StyledTableRow>
      );
    }
    const markup = paginatedVehicles.map(
      ({ id, registration, bankName, model, city }, index) => (
        <StyledTableRow key={index}>
          <StyledTableCell styleProps={{ backgroundColor: 'yellow' }}>
            <h2>{registration}</h2>
          </StyledTableCell>
          <StyledTableCell>{model}</StyledTableCell>
          <StyledTableCell>{bankName}</StyledTableCell>
          <StyledTableCell>{city}</StyledTableCell>
          <StyledTableCell width={'117px'}>
            <input
              type="checkbox"
              onChange={(event) => handleSelectClick(event, id)}
              checked={selected.indexOf(id) !== -1}
            />
          </StyledTableCell>
        </StyledTableRow>
      ),
    );

    if (markup.length === 0) {
      return (
        <StyledTableRow>
          <StyledTableCell colSpan={4} align="center">
            No records found
          </StyledTableCell>
        </StyledTableRow>
      );
    }
    return markup;
  }, [loading, paginatedVehicles, selected, handleSelectClick]);

  if (!vehicles) return <></>;

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        padding: '10px',
      }}
    >
      {showAlert && (
        <Alert severity="success" sx={{ marginBottom: 2 }}>
          Data uploaded successfully
        </Alert>
      )}
      <Grid
        container
        spacing={2}
        justifyContent="center"
        sx={{ width: '100%' }}
      >
        <Grid item xs={12} sm="auto">
          <StyledButton
            onClick={toggleSheetModal}
            startIcon={<CloudUploadIcon />}
            variant="contained"
          >
            Insert data
          </StyledButton>
        </Grid>
      </Grid>
      <Grid container
        spacing={2}
        justifyContent="center"
        sx={{ width: '100%', textAlign: 'center' }}
      >
        <Grid item xs={12} sm="auto">
            <StyledSearchBar
              ref={inputRef}
              id="search-bar"
              label="Search by Registration Number or Bank Name"
              onChange={handleSearchChange}
              value={searchTerm}
              variant="outlined"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              onKeyDown={handleKeyDown}
            />
        </Grid>
      </Grid>
      <Box style={{ width: '100%', maxWidth: '900px', marginTop: '20px' }}>
        <Button
          color="secondary"
          disabled={selected.length === 0}
          onClick={handleDeleteSelected}
          variant="contained"
          fullWidth
          sx={{
            '@media (min-width:600px)': {
              width: 'auto',
            },
          }}
        >
          Delete Selected
        </Button>
        <StyledTableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell>Registration</StyledTableCell>
                <StyledTableCell>Model</StyledTableCell>
                <StyledTableCell>Bank</StyledTableCell>
                <StyledTableCell>City</StyledTableCell>
                <StyledTableCell>
                  <Box style={{ marginBottom: '10px' }}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAllClick}
                      checked={
                        selected.length === filteredVehicles.length &&
                        filteredVehicles.length > 0
                      }
                    />
                    Select All
                  </Box>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>{tableBody}</TableBody>
          </Table>
        </StyledTableContainer>
      </Box>
      <TablePagination
        rowsPerPageOptions={[25, 50]}
        component="div"
        count={filteredVehicles.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {openSheetModal && (
        <VehiclesUpload
          afterSubmit={handleSubmit}
          open={openSheetModal}
          handleClose={toggleSheetModal}
        />
      )}
    </Box>
  );
};
